<script setup lang="ts">
import {ref, computed, watch, onBeforeUnmount, nextTick} from 'vue'
import type {BottomSheetComponentProps} from '../types/BottomSheetComponentProps'
import ButtonComponent from '../../ui/components/ButtonComponent.vue'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import {ColorVariantType} from '../../ui/enum/ColorVariantType'
import {StyleVariantType} from '../../ui/enum/StyleVariantType'
import {useDropdown} from '../composables/useDropdown'
import {useElementBounding} from '@vueuse/core'
import {SizeVariantType} from '../../ui/enum/SizeVariantType'
import {useTouchGestures} from '@toolify/client/src/composables/useTouchGestures'
import {useOverlay} from '../composables/useOverlay'
import {useExperimentalFeatureFlag} from '@toolify/client/src/modules/featureFlags/composables/useExperimentalFeatureFlag'
import {ExperimentalFeatureFlagType} from '@toolify/server/src/models/mongoose/UserModel/enum/ExperimentalFeatureFlagType'
import {useThemeVariables} from '@toolify/client/src/modules/theme/composables/useThemeVariables'
import {ThemeVariableType} from '@toolify/client/src/modules/theme/enum/ThemeVariableType'

interface Emits {
  (e: 'hide'): void
  (e: 'save'): void
}

const props = withDefaults(defineProps<BottomSheetComponentProps>(), {
  isFullscreen: false,
  shouldCloseOnBackdrop: true,
  isOpen: null,
  shouldShowHeader: true,
  shouldShowExit: true,
  shouldShowSave: false,
  allowResize: true,
  isMaxHeightOnAutoMode: true,
  isFullHeightOnAutoMode: false,
  hasHorizontalPadding: true,
})

const emit = defineEmits<Emits>()

const bottomSheetRef = ref<HTMLElement | null>(null)
const referenceRef = ref<HTMLElement | null>(null)
const sheetContentRef = ref<HTMLElement | null>(null)


const {height: measuredHeightPx} = useElementBounding(bottomSheetRef)

const isOpen = ref<boolean>(props.isOpen ?? false)

const {
  addDropdown,
  removeDropdown,
} = useDropdown()

const dropdownId = Symbol()

const {isExperimentalFeatureFlagEnabled} = useExperimentalFeatureFlag()
const shouldShowOverlay = computed(() => !isExperimentalFeatureFlagEnabled.value(ExperimentalFeatureFlagType.HideBottomSheetOverlay))

const {getThemeVariable} = useThemeVariables()
const preferBorders = computed(() => getThemeVariable.value(ThemeVariableType.PreferBorders))
const shouldShowModalHeaderBackgrounds = computed(() => getThemeVariable.value(ThemeVariableType.ShouldShowModalHeaderBackgrounds))

watch(
  () => props.isOpen,
  (value) => {
    isOpen.value = value
    if (value) {
      addDropdown(dropdownId)
    } else {
      removeDropdown(dropdownId)
    }
  },
  {immediate: true},
)

function onOpenEvent() {
  if (typeof props.isOpen === 'boolean') return
  isOpen.value = true
  addDropdown(dropdownId)
}

function onClose() {
  if (typeof props.isOpen !== 'boolean') {
    isOpen.value = false
    removeDropdown(dropdownId)
  }
  emit('hide')
}

const {zIndex} = useOverlay({
  element: bottomSheetRef,
  referenceElement: referenceRef,
  isOpen: isOpen,
  onClose: onClose,
})

const isClosing = ref(false)

function animatedClose() {
  isClosing.value = true
  setTimeout(() => {
    isClosing.value = false
    onClose()
  }, 200)
}

function onSave() {
  emit('save')
}

function onReferenceClick() {
  if (typeof props.isOpen === 'boolean') return
  if (isOpen.value) {
    onClose()
  } else {
    onOpenEvent()
  }
}

watch(referenceRef, (value, oldValue) => {
  oldValue?.removeEventListener('click', onReferenceClick)
  value?.addEventListener('click', onReferenceClick)
})

onBeforeUnmount(() => {
  referenceRef.value?.removeEventListener('click', onReferenceClick)
  removeDropdown(dropdownId)
})


const isAutoMode = ref(true) 
const currentHeight = ref(0) 
const startHeight = ref(0)  
const isTransitioning = ref(false) 


const AUTO_MODE_MAX_RATIO = 0.6

const gestureAllowedInFullMode = ref(true)

async function transitionToAuto() {
  isTransitioning.value = true
  isAutoMode.value = false 


  const winHeight = window.innerHeight
  const maxAutoHeightPx = winHeight * AUTO_MODE_MAX_RATIO
  
  const contentHeight = measuredHeightPx.value || maxAutoHeightPx
  
  const targetHeightPx = Math.min(contentHeight, maxAutoHeightPx)
  
  currentHeight.value = targetHeightPx

  await nextTick() 
  
  isAutoMode.value = true 
  isTransitioning.value = false

}

function transitionToFull() {
  const winHeight = window.innerHeight

  currentHeight.value = winHeight
  isAutoMode.value = false 
}

let scrollBlockerHandler: ((e: TouchEvent) => void) | null = null

const blockScrollInAutoMode = () => {
  if (!bottomSheetRef.value) return
  const scrollArea = bottomSheetRef.value.querySelector('.scroll-area') as HTMLElement
  if (!scrollArea) return
  if (!scrollBlockerHandler) {
    scrollBlockerHandler = (e: TouchEvent) => {
      e.preventDefault()
      e.stopPropagation()
    }
  }
  scrollArea.classList.add('disabled-scroll')
  scrollArea.addEventListener('touchmove', scrollBlockerHandler, {
    passive: false,
    capture: true,
  })
}

const unblockScroll = () => {
  if (!bottomSheetRef.value || !scrollBlockerHandler) return
  const scrollArea = bottomSheetRef.value.querySelector('.scroll-area') as HTMLElement
  if (!scrollArea) return
  scrollArea.classList.remove('disabled-scroll')
  scrollArea.removeEventListener('touchmove', scrollBlockerHandler, {capture: true})
}

const MIN_DRAG_PX = 20

const wasInAutoMode = ref(false)
const hasActivatedDrag = ref(false)
const isTouchOnAutoModeList = ref(false)
const dragOffsetY = ref(0)
const {
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  isDragging,
  isMoving,
} = useTouchGestures({
  onStart: (e) => {
    if (!props.allowResize) return
    if (!bottomSheetRef.value) return

    wasInAutoMode.value = isAutoMode.value

    gestureAllowedInFullMode.value = true

    const target = e.target as HTMLElement
    const scrollArea = bottomSheetRef.value.querySelector('.scroll-area') as HTMLElement
    const isTouchOnScrollArea = scrollArea && scrollArea.contains(target)

    const isFullscreen = !wasInAutoMode.value
    if (isFullscreen && isTouchOnScrollArea) {
      if (scrollArea && scrollArea.scrollTop > 10) {
        gestureAllowedInFullMode.value = false
        return
      }
    }

    hasActivatedDrag.value = false
    dragOffsetY.value = 0
    isTouchOnAutoModeList.value = !isFullscreen && !!isTouchOnScrollArea

    startHeight.value = measuredHeightPx.value
    currentHeight.value = startHeight.value
    /* eslint-disable-next-line @typescript-eslint/no-unused-expressions */
    bottomSheetRef.value?.offsetHeight
  },
  onMove: (deltaX, deltaY, e) => {
    if (!props.allowResize) return
    if (!gestureAllowedInFullMode.value) return

    if (!hasActivatedDrag.value) {
      if (isTouchOnAutoModeList.value && Math.abs(deltaY) < MIN_DRAG_PX) return

      hasActivatedDrag.value = true
      dragOffsetY.value = isTouchOnAutoModeList.value ? deltaY : 0

      if (isTouchOnAutoModeList.value) {
        blockScrollInAutoMode()
      }
      isAutoMode.value = false
    }

    const newHeight = startHeight.value - (deltaY - dragOffsetY.value)

    currentHeight.value = Math.min(window.innerHeight, Math.max(0, newHeight))

    if (!wasInAutoMode.value && deltaY < 0) {
      return
    }

    if (e.cancelable && isDragging.value) e.preventDefault()
  },
  onEnd: (deltaX, deltaY, duration, e) => {
    unblockScroll()
    hasActivatedDrag.value = false
    if (!props.allowResize || !gestureAllowedInFullMode.value) return

    const swipeDistance = deltaY
    const velocity = Math.abs(swipeDistance) / duration

    const VELOCITY_THRESHOLD = 0.5
    const VERY_FAST_VELOCITY = 1.2
    
    const winHeight = window.innerHeight
    
    const EXPAND_THRESHOLD_PX = winHeight * 0.6 
    const CLOSE_THRESHOLD_PX = winHeight * 0.25
    
    const MIN_DURATION = 50
    if (duration < MIN_DURATION || Math.abs(swipeDistance) < MIN_DRAG_PX) {
      currentHeight.value = startHeight.value
      isAutoMode.value = wasInAutoMode.value
      return
    }

    const isFastSwipe = velocity > VELOCITY_THRESHOLD
    const isVeryFastSwipe = velocity > VERY_FAST_VELOCITY
    const isSwipeUp = swipeDistance < -MIN_DRAG_PX
    const isSwipeDown = swipeDistance > MIN_DRAG_PX

    const isStartAutoRange = wasInAutoMode.value

    if (isStartAutoRange) {
      if (isFastSwipe && isSwipeUp) {
        transitionToFull()
      } else if (isFastSwipe && isSwipeDown) {
        animatedClose() 
      } else if (currentHeight.value > EXPAND_THRESHOLD_PX) {
        transitionToFull()
      } else if (currentHeight.value < CLOSE_THRESHOLD_PX) {
        animatedClose() 
      } else {
        transitionToAuto() 
      }
    } else {
      if (currentHeight.value < CLOSE_THRESHOLD_PX) {
        animatedClose()
      } else if (isVeryFastSwipe && isSwipeDown) {
        animatedClose()
      } else if (isSwipeDown || currentHeight.value < EXPAND_THRESHOLD_PX) {
        transitionToAuto()
      } else {
        currentHeight.value = winHeight   
      }
    }
  },
})


const sheetStyle = computed(() => {
  const heightUnit = 'vh'
  const paddingTransition = 'padding-bottom 200ms cubic-bezier(0, .58, .42, 1)'
  const fullScreenMaxHeight = `calc(100${heightUnit} - var(--safe-area-top, env(safe-area-inset-top, 0px)))`
  if (isAutoMode.value && !hasActivatedDrag.value && !isTransitioning.value) {
    const autoModeMaxHeight = `calc(${AUTO_MODE_MAX_RATIO * 100}${heightUnit} + var(--safe-area-bottom-with-keyboard, 0.5rem))`
    return {
      height: props.isFullHeightOnAutoMode && props.isMaxHeightOnAutoMode ? autoModeMaxHeight : 'auto',
      maxHeight: props.isMaxHeightOnAutoMode
        ? autoModeMaxHeight
        : '',
      transform: isClosing.value ? 'translateY(100%)' : 'translateY(0)',
      transition: `max-height 0.45s cubic-bezier(0.22,1,0.36,1), transform 0.2s ease-out, ${paddingTransition}`,
    }
  }

  return {
    height: `${currentHeight.value}px`,
    maxHeight: fullScreenMaxHeight,
    transform: isClosing.value ? 'translateY(100%)' : 'translateY(0)',
    transition: isMoving.value ? '' : `height 0.45s cubic-bezier(0.22,1,0.36,1), transform 0.2s ease-out, ${paddingTransition}`,
  }
})

watch(
  () => isOpen.value,
  (val) => {
    if (val) {
      if (props.isFullscreen) {
        currentHeight.value = window.innerHeight
        isAutoMode.value = false
      } else {
        currentHeight.value = window.innerHeight * AUTO_MODE_MAX_RATIO
        isAutoMode.value = true
      }
    }
  },
  {immediate: true},
)

const gridRows = computed(() => {
  const hasHeader = props.shouldShowHeader
  return hasHeader ? 'auto_1fr' : '1fr'
})

const backdropStyle = computed(() => {
  if (!zIndex.value) {
    return {}
  }
  return {
    zIndex: zIndex.value,
  }
})

const contentStyle = computed(() => {
  if (!zIndex.value) {
    return {}
  }
  return {
    zIndex: zIndex.value + 1,
  }
})

defineExpose({isOpen})
</script>

<template>
  <div class="bottom-sheet-wrapper" ref="referenceRef">
    <slot :isOpen="isOpen"/>

    <Teleport to="#teleported" v-if="isOpen">
      <Transition name="fade" appear>
        <div
          v-if="isOpen && shouldShowOverlay"
          data-overlay-ignore
          class="tw-fixed tw-inset-0 tw-cursor-pointer tw-transition-all tw-duration-300 tw-bg-black/60"
          :style="backdropStyle"
          @click="props.shouldCloseOnBackdrop && !props.isFullscreen ? onClose() : undefined"
        />
      </Transition>
      <Transition name="slide-up" appear>
        <div
          v-if="isOpen"
          class="dropdown-component-content tw-fixed tw-left-0 tw-bottom-0 tw-w-full tw-overflow-hidden"
          :style="contentStyle"
          ref="bottomSheetRef"
          @click.stop
        >
          <div
            class="tw-relative tw-bg-container-low tw-rounded-t-xl tw-shadow-lg tw-grid tw-overflow-hidden"
            :class="[
              `tw-grid-rows-[${gridRows}]`,
              'bottom-sheet-content',
              {'prefer-borders': preferBorders},
            ]"
            :style="[sheetStyle]"
            @touchstart="props.allowResize ? onTouchStart($event) : undefined"
            @touchmove="props.allowResize ? onTouchMove($event) : undefined"
            @touchend="props.allowResize ? onTouchEnd($event) : undefined"
          >
            <div
              v-if="props.allowResize"
              class="tw-w-8 tw-h-1 tw-bg-content-dark tw-rounded-full tw-mx-auto tw-absolute tw-top-2 tw-left-0 tw-right-0 tw-z-30"
            />

            <div
              v-if="props.shouldShowHeader"
              class="tw-relative tw-grid tw-grid-cols-1 tw-items-center tw-h-12 tw-px-3 tw-py-2"
              :class="{
                'tw-bg-container-lowest': shouldShowModalHeaderBackgrounds,
                'tw-border-b-2 tw-border-container': preferBorders,
              }"
            >
              <div class="tw-absolute tw-left-0 tw-top-0 tw-h-full tw-flex tw-items-center">
                <ButtonComponent
                  v-if="props.shouldShowExit"
                  :iconNameType="IconNameType.Xmark"
                  :color="ColorVariantType.Neutral"
                  :style="StyleVariantType.ClearClear"
                  @click="animatedClose"
                />
              </div>

              <div class="tw-flex tw-h-full tw-justify-center tw-items-center tw-w-full tw-min-w-0 tw-pr-12 tw-pl-12">
                <slot name="title">
                  <span class="tw-truncate">
                    {{props.title}}
                  </span>
                </slot>
              </div>

              <div class="tw-absolute tw-right-3 tw-top-0 tw-h-full tw-flex tw-items-center tw-gap-2">
                <slot name="headerActions"/>
                <ButtonComponent
                  v-if="props.shouldShowSave"
                  :color="ColorVariantType.Primary"
                  :style="StyleVariantType.FilledFilled"
                  :size="SizeVariantType.Medium"
                  @click="onSave"
                >
                  Save
                </ButtonComponent>
              </div>
            </div>
            <div
              class="tw-overflow-hidden tw-flex tw-justify-center tw-h-full tw-w-full tw-pt-2"
              :class="{'tw-px-2': props.hasHorizontalPadding}"
              ref="sheetContentRef"
            >
              <slot name="sheet" :isOpen="isOpen" :close="onClose"/>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped lang="scss">
@use '../../../styles/variables' as variables;

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.45s cubic-bezier(0.22, 1, 0.36, 1);
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
}

.bottom-sheet-wrapper {
  width: 100%;
  height: 100%;
  display: grid;
  align-items: center;
}

:deep(.choose-item-container) {
  background: transparent !important;
  padding: 0 !important;

  .input.theme-search.background-normal {
    background: map-get(variables.$colors, surface);
    color: map-get(variables.$colors, content-dark);
  }
}

:deep(.input-wrapper) {
  padding-top: 0 !important;
}

:deep(.handy-workspace-settings) {
  background: transparent !important;
  padding: map-get(variables.$spacing, space-4) map-get(variables.$spacing, space-3) 0 map-get(variables.$spacing, space-3) !important;
  width: 100% !important;
  box-sizing: border-box;
}

.bottom-sheet-content {
  padding-bottom: max(var(--safe-area-bottom-with-keyboard, 0px), 1rem);

  // hairline between menu rows (people picker, context menu, file source sheet)
  &.prefer-borders {
    :deep(.menu-position + .menu-position),
    :deep(.context-menu-item + .context-menu-item) {
      border-top: 1px solid map-get(variables.$colors, container);
    }
  }
}
</style>
